import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Camera, CheckCircle, WarningCircle, ChatCircleDots } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobPhotoRequest() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [uploads, setUploads] = useState([]);   // [{file_id, caption}]
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/public/job-photos/${token}`);
      setState({ loading: false, data: r.data, error: "" });
      if (r.data.completed) setDone(true);
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || "Invalid link" });
    }
  };
  useEffect(() => { load(); }, [token]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      const r = await axios.post(`${API}/api/public/job-photos/${token}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      setUploads([...uploads, { file_id: r.data.file_id, caption }]);
      setCaption("");
      toast.success(`Photo #${r.data.uploaded_count} added`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/job-photos/${token}/finish`, { run_triage: true });
      setDone(true);
      toast.success("Thanks — your property manager has been notified.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  if (state.loading) return <Shell><div className="text-white/70 text-center p-10">Loading…</div></Shell>;
  if (state.error) return <Shell><div className="p-10 text-center">
    <WarningCircle size={40} className="text-red-400 mx-auto"/>
    <div className="mt-2 font-black text-lg text-white">{state.error}</div>
  </div></Shell>;
  const d = state.data;

  return (
    <Shell brand={d.brand_name}>
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-md mx-auto p-4 space-y-4" data-testid="tenant-photo-page">
        <div className="text-white">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1">
            <Camera weight="fill" size={12}/> Photo request
          </div>
          <div className="font-black text-2xl mt-1 leading-tight">{d.title || "Maintenance request"}</div>
          <div className="text-xs text-white/60 mt-1">{d.property_address}{d.unit ? ` · Unit ${d.unit}` : ""}</div>
        </div>

        {d.office_message && (
          <div className="p-3 bg-slate-800 border-l-4 border-emerald-400 text-white text-sm" data-testid="office-msg">
            {d.office_message}
          </div>
        )}

        {done ? (
          <div className="rugged-card p-8 bg-emerald-50 text-center" data-testid="request-done">
            <CheckCircle weight="fill" size={48} className="text-emerald-600 mx-auto"/>
            <div className="mt-2 font-black text-lg text-emerald-800">Thanks — you're done!</div>
            <div className="text-sm text-slate-700 mt-1">Your property manager will follow up with next steps.</div>
          </div>
        ) : (
          <>
            <div className="bg-white p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">
                {uploads.length === 0 ? "Snap 1-3 photos of the issue" : `${uploads.length} photo${uploads.length === 1 ? "" : "s"} added`}
              </div>
              <input type="text" value={caption} onChange={e => setCaption(e.target.value)}
                placeholder="Quick note (optional) — e.g. under the sink" data-testid="upload-caption"
                className="w-full h-10 border-2 border-slate-300 px-2 text-sm outline-none focus:border-slate-900 mb-2"/>
              <label className={`block h-32 border-2 border-dashed border-slate-400 flex items-center justify-center cursor-pointer bg-slate-50 ${busy ? "opacity-50" : "hover:bg-slate-100"}`}
                     data-testid="upload-dropzone">
                <div className="text-center">
                  <Camera size={32} className="mx-auto text-slate-500"/>
                  <div className="mt-1 text-xs font-black uppercase tracking-widest">
                    {busy ? "Uploading…" : "Tap to snap / choose"}
                  </div>
                </div>
                <input ref={inputRef} type="file" accept="image/*" capture="environment"
                  onChange={e => upload(e.target.files?.[0])} disabled={busy}
                  className="hidden" data-testid="upload-input"/>
              </label>
            </div>

            {uploads.length > 0 && (
              <button onClick={finish} disabled={busy} data-testid="finish-btn"
                className="w-full h-14 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50">
                ✓ All done — send to property manager
              </button>
            )}
          </>
        )}

        <div className="text-white/40 text-[10px] text-center pt-4">
          Powered by Field Radar · secure link expires {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "in 7 days"}
        </div>
      </div>
    </Shell>
  );
}


function Shell({ children, brand }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-md mx-auto px-4 pt-4 text-white/50 text-[9px] font-black uppercase tracking-widest">
        {brand || "Property Management"}
      </div>
      {children}
    </div>
  );
}
